import { useEffect, useState } from 'react';
import { IndicadoresService } from '../services/indicadores.service';
import { STORES } from '../data/stores';
import type { Store } from '../data/stores';

const CHAIN_A_CADENA: Record<string, string> = {
  VAZ: 'VAZZA',
  TAF: 'FLEXI',
  MRH: 'MARCHA',
  TRE: 'TRENDER',
};

export interface CatalogoTiendas {
  /** Las tiendas activas, en el mismo formato que usaba el catálogo horneado. */
  stores: Store[];
  /**
   * Las tiendas CERRADAS (SPEC 2026-10-13, punto b), rotuladas «(cerrada)» y
   * con su `cerrada.ultimaActividad`. Cuentan sólo en los periodos en que
   * operaron: ver `utils/catalogoEfectivo.ts`. Vacío contra una API anterior.
   */
  cerradas: Store[];
  cargando: boolean;
  /** `true` si se está usando el catálogo del bundle porque la API falló. */
  usandoRespaldo: boolean;
}

/**
 * Catálogo de tiendas del tablero, servido por el backend.
 *
 * POR QUÉ
 * -------
 * Hasta el 2026-08-11 la lista venía de `data/stores.ts`, horneada en el bundle.
 * Un archivo así sólo está al día si alguien se acuerda de editarlo, y ya había
 * dejado de estarlo: el panel decía "159 tiendas" con 157 activas en el ERP.
 * Las dos de sobra llevaban meses retiradas, y una era la única tienda de una
 * supervisora que ya no colabora — por eso ella seguía saliendo en los filtros.
 *
 * Ahora **Proscai manda sobre qué tiendas existen**; el catálogo local sólo se
 * usa como RESPALDO si la API no responde, para que el panel no se quede en
 * blanco. Cuando eso pasa se marca `usandoRespaldo` y la pantalla lo avisa: un
 * dato viejo sin avisar es peor que no tener dato.
 */
export function useCatalogoTiendas(): CatalogoTiendas {
  // Se ARRANCA con el catálogo local y la API lo corrige al llegar. El panel se
  // abre constantemente y dejarlo en blanco medio segundo, en una pantalla que
  // es sólo filtros y tablas, es peor que mostrar una lista casi idéntica que
  // se corrige sola. `usandoRespaldo` distingue "todavía no llega" de "la API
  // falló y esto es lo único que hay".
  const [stores, setStores] = useState<Store[]>(STORES);
  const [cerradas, setCerradas] = useState<Store[]>([]);
  const [cargando, setCargando] = useState(true);
  const [usandoRespaldo, setUsandoRespaldo] = useState(false);

  useEffect(() => {
    let vivo = true;

    (async () => {
      try {
        const data = await IndicadoresService.getCatalogoTiendas();
        if (!vivo) return;

        const mapeadas: Store[] = data.tiendas.map((t) => {
          const cadena = CHAIN_A_CADENA[t.chain] ?? t.chain;
          return {
            id: `${cadena}#${t.num}`,
            cadena,
            tienda: t.tienda,
            num: String(t.num),
            // El backend devuelve null cuando la tienda existe en el ERP pero
            // no está catalogada. Se muestra el hueco en vez de inventar.
            supervisor: t.supervisor ?? '(sin supervisor)',
            regional: t.regional ?? '(sin regional)',
          };
        });

        setStores(mapeadas);
        setCerradas(
          (data.cerradas ?? []).map((t) => {
            const cadena = CHAIN_A_CADENA[t.chain] ?? t.chain;
            return {
              id: `${cadena}#${t.num}`,
              cadena,
              tienda: `${t.tienda} (cerrada)`,
              num: String(t.num),
              supervisor: t.supervisor ?? '(sin supervisor)',
              regional: t.regional ?? '(sin regional)',
              cerrada: { ultimaActividad: t.ultimaActividad, cerrada: t.cerrada },
            };
          }),
        );
        setUsandoRespaldo(false);
      } catch {
        if (!vivo) return;
        setStores(STORES);
        setUsandoRespaldo(true);
      } finally {
        if (vivo) setCargando(false);
      }
    })();

    return () => {
      vivo = false;
    };
  }, []);

  return { stores, cerradas, cargando, usandoRespaldo };
}
